import { useEffect, useReducer } from "react";
import { screenerRow, type ScreenerRow } from "@helm/ui";
import { marketData, priceFormatOf } from "../md/marketData";

/**
 * Watch every symbol for as long as the caller is mounted and re-render on md frames (coalesced
 * to one per animation frame by the hub). `key` is the joined symbol list, so a new array with the
 * same symbols does not resubscribe.
 */
export function useMarkets(symbols: readonly string[]): void {
  const [, bump] = useReducer((n: number) => n + 1, 0);
  const key = symbols.join(",");
  useEffect(() => {
    const list = key === "" ? [] : key.split(",");
    for (const s of list) marketData.retain(s);
    const offs = list.map((s) => marketData.listen(s, bump));
    return () => {
      for (const off of offs) off();
      for (const s of list) marketData.release(s);
    };
  }, [key]);
}

/**
 * Last / change / volume for one symbol, the screener's rules (@helm/ui screener.ts): change vs
 * the settlement once the gateway streams `SessionStats`, until then vs the first print this
 * window received (`ref: "first"`, labelled in the UI); volume likewise since subscribed.
 */
export function quoteRow(symbol: string): ScreenerRow {
  const m = marketData.get(symbol);
  const f = priceFormatOf(m?.info ?? null);
  return screenerRow({
    symbol,
    contract: m?.info?.symbol ?? null,
    lastTicks: m?.lastTicks ?? null,
    settlementTicks: null, // SessionStats not streamed yet (PLAN TODO)
    firstTicks: m?.firstTicks ?? null,
    volume: m?.volume ?? 0,
    trades: m?.tradeCount ?? 0,
    tickPrice: f ? f.tickSizeNum / f.tickSizeDen : null,
  });
}

/** Session high / low seen since this window subscribed (from the volume-at-price map). */
export function rangeSinceSubscribed(symbol: string): { high: number; low: number } | null {
  const vap = marketData.get(symbol)?.volumeAtPrice;
  if (!vap || vap.size === 0) return null;
  let high = -Infinity;
  let low = Infinity;
  for (const px of vap.keys()) {
    if (px > high) high = px;
    if (px < low) low = px;
  }
  return { high, low };
}
